import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { Button } from "../../components/Button";
import { getAsset } from "../../utilities/assets.utils";
import { HB_WP_API } from "../../utilities/constants.utils";
import { BoardView } from "./components/BoardView";
import { Collections } from "./components/Collections";
import { EmbedSize } from "./components/EmbedSize";
import { GeneralAppearance } from "./components/GeneralAppearance";
import { SuccessMessage } from "./components/SuccessMessage";

/**
 * Once we get the board data, it is upto this component
 * to show the user the options to customize the board.
 * @param {CustomizeBoard.propTypes} props
 */
export const CustomizeBoard = (props) => {
	// These will be available from the board info.
	const { boardCollections, boardName, boardId } = props.data || {};

	const [isNewPage, setIsNewPage] = useState(false);
	const [updatedPageURL, setUpdatedPageURL] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [hasError, setHasError] = useState(false);

	const [formData, setFormData] = useState({
		view: "BB",
		collections: [],
		collectionsUnion: true,
		showPosterName: true,
		showComments: true,
		boardSize: null,
	});

	useEffect(() => {
		if (props.savedConfig) {
			setFormData(props.savedConfig);
		}
	}, [props.savedConfig]);

	/**
	 * Handles the form submit.
	 * We will generate the embed code and send to te API.
	 */
	const onSubmit = async (event) => {
		event.preventDefault();

		if (isLoading) return;

		try {
			// Resetting everything in case of a new attempt.
			setIsNewPage();
			setUpdatedPageURL();
			setIsLoading(true);
			setHasError();

			// We will send everything to the API. API will generate the embed code.
			const apiResponse = await fetch(HB_WP_API + "create", {
				method: "POST",
				body: JSON.stringify({
					...formData,
					boardId,
					boardName,
				}),
			});
			const jsonData = await apiResponse.json();
			if (!jsonData.isSuccess) {
				throw new Error();
			}
			// Once the new page URL is set, we consider the API got succeeded.
			setUpdatedPageURL(jsonData.newPageURL);
			setIsNewPage(jsonData.isNewPage);

			// Once done, we can scroll to the bottom to show the success message.
			window.scrollTo({
				left: 0,
				top: document.body.scrollHeight,
				behavior: "smooth",
			});
		} catch (error) {
			setHasError("Yikes! Something went wrong. Please try again.");
		} finally {
			setIsLoading(false);
		}
	};

	const clearShortCode = async () => {
		try {
			props.onDelete();

			const apiResponse = await fetch(HB_WP_API + "clear", {
				method: "DELETE",
			});
			const jsonData = await apiResponse.json();
			if (!jsonData.isSuccess) {
				throw new Error();
			}
		} catch (error) {
			setHasError("Yikes! Something went wrong. Please try again.");
		} finally {
			setIsLoading(false);
		}
	};

	// Reset the status states when a new board data is getting reset.
	useEffect(() => {
		if (!props.data) {
			setIsNewPage(false);
			setUpdatedPageURL();
			setIsLoading(false);
			setHasError(false);
		}
	}, [props.data]);

	if (!props.data) return null;

	return (
		<>
			<div className="bg-plugin-3-light rounded-sm px-12 sm:px-16 p-8 mb-6">
				<div className="flex flex-col items-center">
					<img
						src={getAsset("customize.png")}
						alt=""
						className="w-16 mb-2"
					/>
					<div className="text-xl font-semibold mt-2">
						Customize your HootBoard
					</div>
					<div className="mt-3 mb-4 text-center leading-6">
						Configure how your HootBoard should be looking in your
						website.
					</div>
				</div>

				<form
					onSubmit={onSubmit}
					className={isLoading ? "pointer-events-none" : ""}
				>
					<BoardView value={formData} onChange={setFormData} />
					<Collections
						boardCollections={boardCollections}
						value={formData}
						onChange={setFormData}
					/>
					<GeneralAppearance
						value={formData}
						onChange={setFormData}
					/>
					<EmbedSize value={formData} onChange={setFormData} />
					<div className="flex justify-between">
						<Button
							label={
								isLoading ? "please wait..." : "Save Changes"
							}
							type="submit"
							color="plugin-3"
						/>
						{props.savedConfig && (
							<Button
								label="Delete"
								onClick={clearShortCode}
								color="plugin-3 bg-opacity-25"
							/>
						)}
					</div>
				</form>
				{hasError && (
					<div className="text-center rounded-md text-red-600 font-bold mt-6">
						{hasError}
					</div>
				)}
			</div>
			<SuccessMessage
				isNewPage={isNewPage}
				updatedPageURL={updatedPageURL}
				hasSavedConfig={!!props.savedConfig}
			/>
		</>
	);
};

CustomizeBoard.defaultProps = {};

CustomizeBoard.propTypes = {
	data: PropTypes.object,
	/**
	 * Already saved embed data.
	 */
	savedConfig: PropTypes.object,
	/**
	 * Once the saved config is deleted, this function will be called
	 */
	onDelete: PropTypes.func,
};
